import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, TrendingUp, Lock, ArrowRight, ExternalLink } from "lucide-react";
import Countdown from "./Countdown";

const DK_AFFILIATE_URL = "https://www.draftkings.com/r/mabecks/US-DK/-";

function PickRow({ pick, locked }) {
  const pct = pick.series_win_1_of_3_pct ?? 0;
  const fg = pick.first_game || {};
  const ml = fg.moneyline;
  return (
    <div
      data-testid={`weekly-pick-${pick.rank}`}
      className="border border-zinc-800 bg-zinc-950/60 p-5 relative overflow-hidden"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <div className="mono text-xs text-zinc-500 tracking-widest">
            #{pick.rank}
          </div>
          <div>
            <div className="font-display text-2xl text-white leading-none">
              {pick.team}
            </div>
            <div className="mono text-[11px] text-zinc-500 mt-1 uppercase tracking-widest">
              {pick.venue_role === "home" ? "HOSTING" : "VISITING"} · {pick.opponent}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl neon-text">{pct}%</div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            ≥1 WIN IN 3
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        {pick.series_dates?.length > 0 && (
          <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest">
            {pick.series_dates.join(" · ")}
          </div>
        )}
        {fg.start_iso && (
          <Countdown
            startIso={fg.start_iso}
            testId={`weekly-pick-countdown-${pick.rank}`}
          />
        )}
      </div>

      {pick.pitching_edge && (
        <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
          <span className="overline neon-text mr-2">PITCHING</span>
          {pick.pitching_edge}
        </p>
      )}

      {/* Lock the deep analysis behind Pro for ranks 3-5 */}
      {locked ? (
        <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
          <Lock size={14} strokeWidth={1.5} className="amber-text mt-0.5" />
          <div className="text-xs text-zinc-400">
            <span className="amber-text mono uppercase tracking-widest text-[10px]">
              PRO · Unlocked on free trial
            </span>
            <div className="mt-1">
              Bullpen break-down, weather notes, public-money split and the full
              Martingale stake plan — 3 days free, then $9.99/week.
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {pick.record_split_note && (
            <p className="text-xs text-zinc-400">
              <span className="overline mr-2">RECORD</span>
              {pick.record_split_note}
            </p>
          )}
          {pick.bullpen_note && (
            <p className="text-xs text-zinc-400">
              <span className="overline mr-2">BULLPEN</span>
              {pick.bullpen_note}
            </p>
          )}
          {pick.weather_note && (
            <p className="text-xs text-zinc-400">
              <span className="overline mr-2">WEATHER</span>
              {pick.weather_note}
            </p>
          )}
          {pick.public_consensus && (
            <p className="text-xs text-zinc-400">
              <span className="overline mr-2">CONSENSUS</span>
              {pick.public_consensus}
            </p>
          )}
          {pick.martingale_case && (
            <p className="text-xs text-[#00D4FF] leading-relaxed mt-2">
              <span className="overline mr-2">MARTINGALE</span>
              {pick.martingale_case}
            </p>
          )}
        </div>
      )}

      <a
        href={DK_AFFILIATE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`weekly-pick-dk-${pick.rank}`}
        className="mt-4 flex items-center justify-center gap-2 py-3 bg-[#53d337] hover:bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs transition-colors"
      >
        Lock game 1 on DraftKings
        {typeof ml === "number" && (
          <span className="mono text-sm">
            · {pick.team.split(" ").slice(-1)[0]} {ml > 0 ? `+${ml}` : ml}
          </span>
        )}
        <ExternalLink size={12} strokeWidth={2} />
      </a>
    </div>
  );
}

export default function WeeklyPicks() {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer;
    async function load(attempt = 0) {
      try {
        const { data } = await api.get("/picks/weekly/mlb");
        if (!cancelled) setPack(data);
        // If server is still generating the cache, try again in 20s.
        if (data?.generating && attempt < 4) {
          timer = setTimeout(() => load(attempt + 1), 20000);
        }
      } catch (err) {
        console.warn("weekly picks fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <section
      className="relative border-t border-zinc-800 bg-zinc-950/40"
      data-testid="weekly-picks-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline neon-text mb-3 flex items-center gap-2">
              <TrendingUp size={12} strokeWidth={1.5} /> THIS·WEEK·ON·THE·BOARD
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight">
              Top 5 MLB series plays.
            </h2>
            <p className="mt-4 text-zinc-400 max-w-2xl text-sm lg:text-base">
              AI-graded teams with the highest probability of winning at least one game
              of their next 3-game series — the exact setup Martingale progression was
              built for. Synthesized from DraftKings lines, pitcher matchups, home/road
              splits, bullpen form and weather.
            </p>
          </div>
          <Link
            to="/register"
            data-testid="weekly-picks-cta"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs hover:brightness-110"
          >
            Start 3 Free Days <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        {loading && !pack ? (
          <div className="flex items-center gap-2 text-zinc-400 mono text-sm py-8">
            <Loader2 size={16} className="animate-spin text-[#00D4FF]" /> Analyzing
            this week's slate…
          </div>
        ) : !pack?.picks?.length ? (
          <div className="border border-dashed border-zinc-800 p-10 text-center">
            <p className="text-zinc-400">
              {pack?.headline ||
                "No MLB series on the board this week — check back tomorrow."}
            </p>
          </div>
        ) : (
          <>
            {pack.headline && (
              <p className="mb-6 text-sm text-zinc-300 border-l-2 border-[#00D4FF] pl-4">
                {pack.headline}
              </p>
            )}
            <div className="grid lg:grid-cols-2 gap-4">
              {pack.picks.slice(0, 5).map((pick) => (
                <PickRow
                  key={pick.rank}
                  pick={pick}
                  locked={pick.rank >= 3}
                />
              ))}
            </div>
            <div className="mt-10 border border-[#00D4FF]/30 bg-[#00D4FF]/5 p-6 flex flex-wrap justify-between items-center gap-4">
              <div>
                <div className="overline neon-text mb-1">UNLOCK ALL 5 PLAYS</div>
                <p className="text-sm text-zinc-300">
                  Start a 3-day free trial to see the full analysis on every pick plus
                  the live arbitrage scanner, morning email digest and unlimited daily
                  plays.
                </p>
              </div>
              <Link
                to="/register"
                data-testid="weekly-picks-bottom-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs hover:brightness-110"
              >
                Get the full card <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
